// Loader Component
// This file defines a loading and error wrapper for async content, using Forge UI components.

import { PropsWithChildren } from "react";
import { styled } from "@stitches/react";
import { Loader as ForgeLoader, Banner, BannerItem } from "@Forge/dist";

/**
 * LoaderContainer
 * Styled container for centering the loader and setting min height.
 */
const LoaderContainer = styled("div", {
  padding: 20,
  minHeight: 200,
});

/**
 * Props for the Loader component
 * @param isLoading - Whether to show the loading spinner
 * @param isError - Whether to show the error banner
 * @param errorReason - Error message to display
 * @param displayText - Text to display with the loader
 * @param children - Content to render when not loading or error
 */
const Loader = ({
  isLoading,
  isError = false,
  errorReason = "",
  displayText,
  children,
}: PropsWithChildren<{
  isLoading: boolean;
  isError?: boolean;
  errorReason?: string;
  displayText: string;
}>) => {
  return isLoading ? (
    // Show loading spinner and text
    <LoaderContainer>
      <ForgeLoader loading={isLoading} fixedCenter={true} text={displayText} />
    </LoaderContainer>
  ) : (
    <>
      {isError ? (
        // Show error banner if error
        <Banner alertType="critical">
          <BannerItem headerText="Network Error">{errorReason}</BannerItem>
        </Banner>
      ) : (
        // Render children if not loading or error
        children
      )}
    </>
  );
};

export default Loader;
